import { Vector3 } from "three";
export const G = 6.6743e-11;

export class Body {
    /**
     * Mass of the body in kilograms
     */
    mass: number;
    /**
     * Position of the body in meters
     */
    pos: Vector3;
    /**
     * Velocity of the body in meter per second
     */
    vel = new Vector3(0, 0, 0);

    constructor(mass: number, pos: Vector3, vel?: Vector3) {
        this.mass = mass;
        this.pos = pos;
        if (vel) this.vel = vel;
    }

    /**
     * Calculates the gravitational force between two objects and updates their positions and velocities accordingly
     * @param obj1 The first object
     * @param obj2 The second object
     * @param dt The delta time
     */
    static calcGrav(obj1: Body, obj2: Body, dt: number) {
        if(dt === 0) return;
        
        // calculate the gravitational force (gamma)
        const gamma1 = new Vector3(
            G * obj2.mass * (obj2.pos.x - obj1.pos.x),
            G * obj2.mass * (obj2.pos.y - obj1.pos.y),
            G * obj2.mass * (obj2.pos.z - obj1.pos.z)
        );

        const gamma2 = new Vector3(
            -G * obj1.mass * (obj2.pos.x - obj1.pos.x),
            -G * obj1.mass * (obj2.pos.y - obj1.pos.y),
            -G * obj1.mass * (obj2.pos.z - obj1.pos.z)
        );

        const d = obj1.pos.distanceTo(obj2.pos) ** 3;

        // normalize the vectors
        gamma1.divideScalar(d);
        gamma2.divideScalar(d);

        obj1.#update(gamma1, dt);
        obj2.#update(gamma2, dt);
    }

    #update(gamma: Vector3, dt: number) {
        // calculate new position and velocity
        // 1.: set up the vectors
        const addPos = this.vel.clone();
        const addGamma = gamma.clone();
        const addGammaVel = gamma.clone();

        // 2.: perform the calculations
        addPos.multiplyScalar(dt);
        addGamma.multiplyScalar(0.5 * dt ** 2);
        addGammaVel.multiplyScalar(dt);

        // 3.: add the vectors to the object
        this.pos.add(addPos);
        this.pos.add(addGamma);
        this.vel.add(addGammaVel);
    }
}
